/**
 * A network's settings, and the block on a network delete (app#376, docs/PERMISSIONS.md § 5
 * Network and § 6).
 *
 * Pure, so `networkSettings.test.ts` checks it with `node --experimental-strip-types`. The store and
 * the pages call `callTool` / PostgREST with what these return.
 */

/** The two settings a network can be created with. */
export interface NetworkCreateOptions {
  /** Client isolation: the network's nodes do not reach each other. Default off. */
  isolated?: boolean
  /** Any member of the org may add a node to the network. Default on. */
  membersMayAddNodes?: boolean
}

/**
 * `network_create` arguments.
 *
 * ⚠ A setting is sent ONLY when it differs from the router's default (`isolated: false`,
 * `members_may_add_nodes: true`). A create with the defaults is then the same call it always was,
 * and a router that does not know the field yet (before router#1769 / router#1761) still creates
 * the network the reader asked for. A reader who asked for a non-default gets a refusal instead of
 * a network without the setting.
 * ⚠ `org_id` is omitted, never sent empty, when there is no active org.
 */
export function networkCreateArgs(
  name: string,
  orgId: string | null | undefined,
  options: NetworkCreateOptions = {}
): Record<string, unknown> {
  const args: Record<string, unknown> = { name }
  if (orgId) args.org_id = orgId
  if (options.isolated === true) args.isolated = true
  if (options.membersMayAddNodes === false) args.members_may_add_nodes = false
  return args
}

/**
 * `network_update` arguments to turn client isolation on or off.
 * ⚠ `network_id`, never `network_name`: a name can be the same in two of the reader's orgs.
 */
export function networkIsolationArgs(networkId: string, isolated: boolean): Record<string, unknown> {
  return { network_id: networkId, isolated }
}

/**
 * `network_update` arguments to turn "members may add nodes" on or off (router#1761).
 * ⚠ `network_id`, never `network_name`: a name can be the same in two of the reader's orgs.
 */
export function networkMembersMayAddNodesArgs(networkId: string, membersMayAddNodes: boolean): Record<string, unknown> {
  return { network_id: networkId, members_may_add_nodes: membersMayAddNodes }
}

/** One line under the isolation switch, for each state. */
export function isolationLine(isolated: boolean): string {
  return isolated
    ? 'On: the nodes in this network cannot reach each other. Services published through noBGP still work, and each node keeps its own tools.'
    : 'Off: the nodes in this network reach each other.'
}

/**
 * One line for the "members may add nodes" setting.
 *
 * ⚠ `undefined` is ON: it is the column default, and a database without router migration `0230` has
 * no such column, where every member may add a node (the rule before the setting).
 * ⚠ ON is a REACH grant (PERMISSIONS.md § 5 Network, decided F-4), and the line says so.
 */
export function membersMayAddNodesLine(membersMayAddNodes: boolean | null | undefined): string {
  return membersMayAddNodes === false
    ? 'Off: only the owner of this network and the owners and admins of the organization add nodes to it.'
    : 'On: any member of the organization may add a node to this network, and that node then reaches the other nodes in it.'
}

/** The line a member sees when the setting keeps them from adding a node. */
export const ADD_NODE_REFUSED =
  'Only the owner of this network, or an owner or admin of its organization, can add nodes to it.'

